import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectMWComparatorControlPanelArea,
  selectMWComparatorControlPanelDates,
  selectMWComparatorControlPanelModel,
  selectMWComparatorControlPanelModelPeriods,
  selectMWComparatorControlPanelVariable,
} from 'redux/reducers/meteorologyWeatherComparatorControlPanelReducer';
import { GetImagesParams } from 'types/Meteorology/Weather/SeasonalComparator/getImages';
import simpleFormattedDateToDiff from 'helper/simpleFormattedDateToDiff';
import useSeasonalComparatorOptions from './useSeasonalComparatorOptions';

const MODEL_DATE_FORMAT = 'yyyy-MM';
const PERIOD_DATE_FORMAT = 'yyyy-MM';

export default function useImagesParams(): GetImagesParams | undefined {
  const { options } = useSeasonalComparatorOptions();
  const variable = useSelector(selectMWComparatorControlPanelVariable);
  const area = useSelector(selectMWComparatorControlPanelArea);
  const models = useSelector(selectMWComparatorControlPanelModel);
  const dates = useSelector(selectMWComparatorControlPanelDates);
  const modelPeriods = useSelector(selectMWComparatorControlPanelModelPeriods);

  const params = useMemo(() => {
    const validatedVariable = options?.find((v) => v.id === variable);
    const validatedArea = validatedVariable?.areas?.find((a) => a.id === area);
    const modelsAreValid = models?.every(
      (m) => validatedArea?.models?.find((model) => model.id === m) !== undefined,
    );
    const datesAreValid = dates?.every((date) => date !== undefined);
    const periodsAreValid = modelPeriods?.every((period) => period.startDate);
    const paramsAreValid = modelsAreValid && datesAreValid && periodsAreValid;

    if (!paramsAreValid) return undefined;

    return {
      variable,
      area,
      confrontingModel: {
        roundDate: simpleFormattedDateToDiff(dates[0], MODEL_DATE_FORMAT),
        model: models[0],
        initialYearMonth: simpleFormattedDateToDiff(modelPeriods[0].startDate, PERIOD_DATE_FORMAT),
        finalYearMonth: simpleFormattedDateToDiff(
          modelPeriods[0].endDate || modelPeriods[0].startDate,
          PERIOD_DATE_FORMAT,
        ),
      },
      baseModel: {
        roundDate: simpleFormattedDateToDiff(dates[1], MODEL_DATE_FORMAT),
        model: models[1],
        initialYearMonth: simpleFormattedDateToDiff(modelPeriods[1].startDate, PERIOD_DATE_FORMAT),
        finalYearMonth: simpleFormattedDateToDiff(
          modelPeriods[1].endDate || modelPeriods[1].startDate,
          PERIOD_DATE_FORMAT,
        ),
      },
    };
  }, [options, variable, area, models, dates, modelPeriods]);

  return params;
}
